import {
  createContext,
  FC,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';
import { HierarchyRFMConfigContext as HierarchyRFMConfigContextType } from '../interfaces/HierarchyRFMConfigContextType';
import useUser from '../../../hooks/useUser';
import { HierarchyRFMConfigType } from '../interfaces/HierarchyRFMConfigType';
import {
  deleteRFMConfig,
  getRFMConfig,
  updateRFMConfig,
} from '../../RFMConfig/services';
import { RFMConfigLevel } from '../../RFMConfig/interfaces/RFMConfigLevel';
import toast from 'react-hot-toast';
import TenantField from '../../../interfaces/TenantField';
import { isEmpty } from 'lodash';
import { ProductCombination } from '../../../interfaces/ProductCombination';
import { getProductHierarchyValues } from '../../../services/product';
import { useTranslations } from 'use-intl';

export const HierarchyRFMConfigContext =
  createContext<HierarchyRFMConfigContextType>({
    hierarchyRFMConfig: undefined,
    setHierarchyRFMConfig(value) {},
    hierarchyRFMConfigLoading: true,
    hierarchyRFMConfigError: null,

    async updateConfig(value) {},
    async deleteConfig() {},

    selectedHierarchy: undefined,
    setSelectedHierarchy(value) {},

    hierarchyValues: [],
    hierarchyValuesError: null,
    hierarchyValuesLoading: true,
  });

export const HierarchyRFMConfigProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { selectedBusiness } = useUser();
  const t = useTranslations('hierarchyRfmConfig');

  const [selectedHierarchy, setSelectedHierarchy] = useState<TenantField>();

  const [config, setConfig] = useState<HierarchyRFMConfigType>();
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState<string | null>(null);

  const [hierarchyValues, setHierarchyValues] = useState<ProductCombination[]>(
    []
  );
  const [hierarchyValuesLoading, setHierarchyValuesLoading] =
    useState<boolean>(true);
  const [hierarchyValuesError, setHierarchyValuesError] = useState<
    string | null
  >(null);

  useEffect(() => {
    const abortController = new AbortController();

    setConfig(undefined);
    setConfigLoading(true);
    setConfigError(null);

    setSelectedHierarchy(undefined);

    setHierarchyValues([]);
    setHierarchyValuesLoading(true);
    setHierarchyValuesError(null);

    getRFMConfig(selectedBusiness.id, 'CF1', abortController) //default value
      .then((data) => {
        if (!isEmpty(data)) {
          setConfig(data as HierarchyRFMConfigType);

          const hierarchy = selectedBusiness.productHierarchy.find(
            (h) => h.key === (data as HierarchyRFMConfigType).field
          ) as TenantField;
          setSelectedHierarchy(hierarchy);
        }
      })
      .catch(() => setConfigError(t('loadConfigError')))
      .finally(() => {
        setConfigLoading(false);
        setHierarchyValuesLoading(false);
      });
    return () => abortController.abort();
  }, [selectedBusiness, t]);

  useEffect(() => {
    const abortController = new AbortController();
    if (!selectedHierarchy) return;

    setHierarchyValuesLoading(true);
    setHierarchyValuesError(null);
    getProductHierarchyValues({
      hierarchies: [selectedHierarchy.key],
      businessUnits: [selectedBusiness],
      abortController,
    })
      .then((values) => setHierarchyValues(values))
      .catch(() => setHierarchyValuesError(t('loadValuesError')))
      .finally(() => setHierarchyValuesLoading(false));

    return () => abortController.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedHierarchy]);

  const updateConfig = async (newConfig: HierarchyRFMConfigType) => {
    updateRFMConfig(selectedBusiness.id, {
      level: RFMConfigLevel.PRODUCT_HIERARCHY,
      value: newConfig,
    })
      .then((data) => setConfig(data))
      .catch(() => toast.error(t('updateError')));
  };

  const deleteConfig = async () => {
    deleteRFMConfig(selectedBusiness.id, RFMConfigLevel.PRODUCT_HIERARCHY)
      .then(() => {
        toast.success(t('configDeleted'));
        setConfig(undefined);
        setSelectedHierarchy(undefined);
      })
      .catch(() => toast.error(t('deleteError')));
  };

  return (
    <HierarchyRFMConfigContext.Provider
      value={{
        hierarchyRFMConfig: config,
        setHierarchyRFMConfig: setConfig,
        hierarchyRFMConfigLoading: configLoading,
        hierarchyRFMConfigError: configError,

        updateConfig,
        deleteConfig,

        selectedHierarchy,
        setSelectedHierarchy,

        hierarchyValues,
        hierarchyValuesLoading,
        hierarchyValuesError,
      }}
    >
      {children}
    </HierarchyRFMConfigContext.Provider>
  );
};
